import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart, faLocationDot, faLink, faUtensils, faPhone, faStar } from '@fortawesome/free-solid-svg-icons';

const formatDistance = (miles) => {
    if (miles == null) return null;
    if (miles < 0.1) return '<0.1 mi';
    if (miles < 10)  return `${miles.toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
};

const PRICE_MAP = {
    PRICE_LEVEL_INEXPENSIVE: '$',
    PRICE_LEVEL_MODERATE: '$$',
    PRICE_LEVEL_EXPENSIVE: '$$$',
    PRICE_LEVEL_VERY_EXPENSIVE: '$$$$',
};

const STOP_WORDS = new Set([
    'restaurant', 'house', 'kitchen', 'cafe', 'express', 'garden', 'palace',
    'bistro', 'grill', 'bar', 'the', 'and', 'of', 'new', 'good', 'golden',
    'lucky', 'happy', 'royal', 'imperial', 'asian', 'american', 'chinese',
    'japanese', 'thai', 'korean', 'indian', 'italian', 'mexican', 'french',
    'vietnamese', 'mediterranean', 'eastern', 'western', 'north', 'south',
    'east', 'west', 'city', 'village', 'town', 'hong', 'kong', 'panda',
]);

const SOCIAL_DOMAINS = new Set([
    'instagram.com', 'facebook.com', 'twitter.com', 'x.com',
    'yelp.com', 'tripadvisor.com', 'wix.com', 'squarespace.com',
    'linktr.ee', 'linktree.com',
]);

const fetchLogo = (websiteUrl, onSuccess) => {
    try {
        const domain = new URL(websiteUrl).hostname.replace(/^www\./, '');
        if (SOCIAL_DOMAINS.has(domain)) return;
        const url = `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${domain}&size=128`;
        const img = new Image();
        img.onload = () => { if (img.naturalWidth >= 32) onSuccess(url); };
        img.onerror = () => {};
        img.src = url;
    } catch (_) {}
};

const RestaurantCard = ({ restaurant, onIdCollected }) => {
    const [imgFallback, setImgFallback] = useState(0);
    const [googlePhotoUrl, setGooglePhotoUrl] = useState(null);
    const [websiteLogoUrl, setWebsiteLogoUrl] = useState(null);
    const [mapillaryUrl, setMapillaryUrl] = useState(null);
    const [namePhotoUrl, setNamePhotoUrl] = useState(null);
    const [cuisinePhotoUrl, setCuisinePhotoUrl] = useState(null);
    const [rating, setRating]           = useState(restaurant.stars || null);
    const [ratingCount, setRatingCount] = useState(restaurant.ratingCount || null);
    const [googleAddress, setGoogleAddress] = useState(null);
    const [googleWebsite, setGoogleWebsite] = useState(null);
    const [googlePhone, setGooglePhone] = useState(null);
    const [openNow, setOpenNow] = useState(null);
    const [priceLevel, setPriceLevel] = useState(null);
    const [googleMapsUri, setGoogleMapsUri] = useState(null);
    const [businessStatus, setBusinessStatus] = useState(null);
    const [popularDishes, setPopularDishes] = useState(null);

    // Logo from OSM website domain
    useEffect(() => {
        if (restaurant.website) fetchLogo(restaurant.website, setWebsiteLogoUrl);
    }, []); // eslint-disable-line

    // Fallback: logo from Google Places website if OSM had no website
    useEffect(() => {
        if (googleWebsite && !websiteLogoUrl) fetchLogo(googleWebsite, setWebsiteLogoUrl);
    }, [googleWebsite]); // eslint-disable-line

    // Google Places — photo, rating, address, website, phone, open status, price level, maps link
    useEffect(() => {
        const key = process.env.REACT_APP_GOOGLE_PLACES_KEY;
        if (!key || !restaurant.lat || !restaurant.lon) return;
        const cacheKey = `gplaces4_${restaurant.id}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            try {
                const c = JSON.parse(cached);
                if (c.photoUrl)        setGooglePhotoUrl(c.photoUrl);
                if (c.rating)          setRating(c.rating);
                if (c.ratingCount)     setRatingCount(c.ratingCount);
                if (c.address)         setGoogleAddress(c.address);
                if (c.website)         setGoogleWebsite(c.website);
                if (c.phone)           setGooglePhone(c.phone);
                if (c.openNow != null) setOpenNow(c.openNow);
                if (c.priceLevel)      setPriceLevel(c.priceLevel);
                if (c.googleMapsUri)   setGoogleMapsUri(c.googleMapsUri);
                if (c.businessStatus)  setBusinessStatus(c.businessStatus);
            } catch (_) {}
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch('https://places.googleapis.com/v1/places:searchText', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': key,
                        'X-Goog-FieldMask': 'places.photos,places.rating,places.userRatingCount,places.formattedAddress,places.websiteUri,places.currentOpeningHours,places.priceLevel,places.googleMapsUri,places.nationalPhoneNumber,places.businessStatus',
                    },
                    body: JSON.stringify({
                        textQuery: restaurant.name,
                        locationRestriction: {
                            rectangle: {
                                low:  { latitude: restaurant.lat - 0.005, longitude: restaurant.lon - 0.006 },
                                high: { latitude: restaurant.lat + 0.005, longitude: restaurant.lon + 0.006 },
                            },
                        },
                        maxResultCount: 1,
                    }),
                });
                const data = await res.json();
                const place = data.places?.[0];
                const photoName = place?.photos?.[0]?.name;
                const photoUrl = photoName
                    ? `https://places.googleapis.com/v1/${photoName}/media?maxWidthPx=600&key=${key}`
                    : null;
                const toCache = {
                    photoUrl:       photoUrl || null,
                    rating:         place?.rating ?? null,
                    ratingCount:    place?.userRatingCount ?? null,
                    address:        place?.formattedAddress || null,
                    website:        place?.websiteUri || null,
                    phone:          place?.nationalPhoneNumber || null,
                    openNow:        place?.currentOpeningHours?.openNow ?? null,
                    priceLevel:     place?.priceLevel || null,
                    googleMapsUri:  place?.googleMapsUri || null,
                    businessStatus: place?.businessStatus || null,
                };
                sessionStorage.setItem(cacheKey, JSON.stringify(toCache));
                if (!cancelled) {
                    if (toCache.photoUrl)        setGooglePhotoUrl(toCache.photoUrl);
                    if (toCache.rating)          setRating(toCache.rating);
                    if (toCache.ratingCount)     setRatingCount(toCache.ratingCount);
                    if (toCache.address)         setGoogleAddress(toCache.address);
                    if (toCache.website)         setGoogleWebsite(toCache.website);
                    if (toCache.phone)           setGooglePhone(toCache.phone);
                    if (toCache.openNow != null) setOpenNow(toCache.openNow);
                    if (toCache.priceLevel)      setPriceLevel(toCache.priceLevel);
                    if (toCache.googleMapsUri)   setGoogleMapsUri(toCache.googleMapsUri);
                    if (toCache.businessStatus)  setBusinessStatus(toCache.businessStatus);
                }
            } catch (_) {
                sessionStorage.setItem(cacheKey, JSON.stringify({}));
            }
        })();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line

    // Food photo from keywords in the restaurant name (e.g. "Noodle House" → searches "noodle")
    useEffect(() => {
        const words = restaurant.name
            .toLowerCase()
            .replace(/[^a-z\s]/g, ' ')
            .split(/\s+/)
            .filter(w => w.length > 3 && !STOP_WORDS.has(w));
        if (!words.length) return;
        let cancelled = false;
        (async () => {
            for (const word of words) {
                if (cancelled) return;
                const cacheKey = `mealurls_${word}`;
                const cached = sessionStorage.getItem(cacheKey);
                let urls;
                if (cached) {
                    try { urls = JSON.parse(cached); } catch (_) { urls = []; }
                } else {
                    try {
                        const res = await fetch(`https://www.themealdb.com/api/json/v1/1/search.php?s=${encodeURIComponent(word)}`);
                        const data = await res.json();
                        urls = (data.meals || []).slice(0, 10).map(m => m.strMealThumb).filter(Boolean);
                        sessionStorage.setItem(cacheKey, JSON.stringify(urls));
                    } catch (_) { urls = []; }
                }
                if (urls.length > 0 && !cancelled) {
                    setNamePhotoUrl(urls[Math.floor(Math.random() * urls.length)]);
                    return;
                }
            }
        })();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line

    // Fetch cuisine-specific random food photo from TheMealDB for visual variety
    useEffect(() => {
        const cuisine = (restaurant.cuisine || restaurant.foodName || '').split(/[,;]/)[0].trim();
        if (!cuisine) return;
        const cacheKey = `mealurls_${cuisine.toLowerCase()}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            try {
                const urls = JSON.parse(cached);
                if (urls.length > 0) setCuisinePhotoUrl(urls[Math.floor(Math.random() * urls.length)]);
            } catch (_) {}
            return;
        }
        fetch(`https://www.themealdb.com/api/json/v1/1/search.php?s=${encodeURIComponent(cuisine)}`)
            .then(r => r.json())
            .then(data => {
                const urls = (data.meals || []).slice(0, 10).map(m => m.strMealThumb).filter(Boolean);
                sessionStorage.setItem(cacheKey, JSON.stringify(urls));
                if (urls.length > 0) setCuisinePhotoUrl(urls[Math.floor(Math.random() * urls.length)]);
            })
            .catch(() => {});
    }, []); // eslint-disable-line

    // Fetch nearby Mapillary street-level photo
    useEffect(() => {
        const key = process.env.REACT_APP_MAPILLARY_KEY;
        if (!key || !restaurant.lat || !restaurant.lon) return;
        const cacheKey = `mapillary_${restaurant.id}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) { setMapillaryUrl(cached === 'none' ? null : cached); return; }
        const d = 0.0005;
        const bbox = `${restaurant.lon - d},${restaurant.lat - d},${restaurant.lon + d},${restaurant.lat + d}`;
        fetch(`https://graph.mapillary.com/images?fields=id,thumb_256_url&bbox=${bbox}&limit=1&access_token=${key}`)
            .then(r => r.json())
            .then(data => {
                const url = data.data?.[0]?.thumb_256_url || null;
                sessionStorage.setItem(cacheKey, url || 'none');
                setMapillaryUrl(url);
            })
            .catch(() => sessionStorage.setItem(cacheKey, 'none'));
    }, []); // eslint-disable-line

    // Ask Claude Haiku for 3 popular/signature dishes at this restaurant
    useEffect(() => {
        const key = process.env.REACT_APP_ANTHROPIC_API_KEY;
        if (!key) return;
        const cacheKey = `dishes_${restaurant.id}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            try { setPopularDishes(JSON.parse(cached)); } catch (_) {}
            return;
        }
        const cuisine = (restaurant.cuisine || restaurant.foodName || '');
        const prompt = `List 3 popular or signature dishes at "${restaurant.name}"${cuisine ? `, a ${cuisine} restaurant` : ''}. Reply with ONLY a JSON array of 3 short dish names, for example: ["Dish One","Dish Two","Dish Three"].`;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch('https://api.anthropic.com/v1/messages', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': key,
                        'anthropic-version': '2023-06-01',
                        'anthropic-dangerous-direct-browser-access': 'true',
                    },
                    body: JSON.stringify({
                        model: 'claude-haiku-4-5-20251001',
                        max_tokens: 100,
                        messages: [{ role: 'user', content: prompt }],
                    }),
                });
                const data = await res.json();
                const text = data.content?.[0]?.text || '';
                const match = text.match(/\[.*?\]/s);
                if (match) {
                    const dishes = JSON.parse(match[0]);
                    sessionStorage.setItem(cacheKey, JSON.stringify(dishes));
                    if (!cancelled) setPopularDishes(dishes);
                }
            } catch (_) {}
        })();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line

    // Priority: Google Places → OSM photo → chain logo → searched dish photo → Mapillary street → food from name → food from cuisine
    const photoSources = [
        googlePhotoUrl,
        restaurant.restaurantImage,
        websiteLogoUrl,
        restaurant.foodImage,
        mapillaryUrl,
        namePhotoUrl,
        cuisinePhotoUrl,
    ].filter(Boolean);
    const photoUrl = imgFallback < photoSources.length ? photoSources[imgFallback] : null;

    useEffect(() => {
        if (onIdCollected) onIdCollected(restaurant.id, restaurant);
    }, [restaurant, onIdCollected]);

    const mapsUrl = googleMapsUri || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.name + (restaurant.address ? ' ' + restaurant.address : ''))}`;
    const address = googleAddress || restaurant.address || '';
    const menuUrl = googleWebsite || restaurant.website ||
        `https://www.google.com/search?q=${encodeURIComponent(restaurant.name + (address ? ' ' + address : '') + ' menu')}`;
    const phone = googlePhone || restaurant.phone;
    const isClosed = businessStatus === 'CLOSED_PERMANENTLY';

    return (
        <div className={`flex justify-between gap-x-6 py-5 border-b border-orange-900${isClosed ? ' opacity-50' : ''}`}>
            <div className="flex min-w-0 gap-x-4">
                <div className="flex-none">
                    <a href={mapsUrl} target="_blank" rel="noreferrer"
                       restaurantid={restaurant.id} restaurantname={restaurant.name}>
                        {photoUrl ? (
                            <img src={photoUrl} alt={restaurant.name} className="Food_Photo"
                                 onError={() => setImgFallback(prev => prev + 1)} />
                        ) : (
                            <div className="Food_Photo_Placeholder">
                                <span className="text-3xl">{restaurant.foodEmoji || '🍽️'}</span>
                            </div>
                        )}
                    </a>
                </div>
                <div className="min-w-0 flex-auto text-xs">
                    <p className="text-base font-semibold leading-6 text-white flex items-center gap-1.5">
                        {websiteLogoUrl && (
                            <img src={websiteLogoUrl} alt="" className="w-5 h-5 rounded-sm object-contain shrink-0" />
                        )}
                        <a href={mapsUrl} target="_blank" rel="noreferrer"
                           className="font-medium text-white hyphens-auto hover:text-orange-400"
                           restaurantid={restaurant.id} restaurantname={restaurant.name}>
                            {restaurant.name}
                        </a>
                    </p>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                        {restaurant.cuisine && (
                            <span className="text-sm font-semibold text-orange-300 capitalize">{restaurant.cuisine}</span>
                        )}
                        {priceLevel && PRICE_MAP[priceLevel] && (
                            <span className="text-xs text-gray-400">{PRICE_MAP[priceLevel]}</span>
                        )}
                        {!isClosed && openNow != null && (
                            <span className={`text-xs font-medium ${openNow ? 'text-emerald-400' : 'text-red-400'}`}>
                                {openNow ? 'Open' : 'Closed'}
                            </span>
                        )}
                        {isClosed && (
                            <span className="text-xs font-medium text-red-500">Permanently closed</span>
                        )}
                    </div>
                    {rating != null && (
                        <p className="text-xs text-yellow-400 mt-0.5">
                            <FontAwesomeIcon icon={faStar} size="xs" className="mr-1" />
                            {typeof rating === 'number' ? rating.toFixed(1) : rating}
                            {ratingCount && <span className="text-gray-500 ml-1">({ratingCount.toLocaleString()})</span>}
                        </p>
                    )}
                    {((googleAddress || restaurant.address) || restaurant.distanceMiles != null) && (
                        <p className="text-xs text-gray-400 mt-0.5">
                            <FontAwesomeIcon icon={faLocationDot} size="xs" className="mr-1" />
                            {[googleAddress || restaurant.address, formatDistance(restaurant.distanceMiles)].filter(Boolean).join(' · ')}
                        </p>
                    )}
                    {restaurant.opening_hours && (
                        <p className="text-xs text-gray-400 mt-0.5">{restaurant.opening_hours}</p>
                    )}
                    {phone && (
                        <p className="text-xs text-gray-400 mt-0.5">
                            <FontAwesomeIcon icon={faPhone} size="xs" className="mr-1" />
                            {phone}
                        </p>
                    )}
                    <div className="mt-2 space-y-1.5">
                        <div>
                            <a href={menuUrl} target="_blank" rel="noreferrer"
                               className="Menu_Button"
                               restaurantid={restaurant.id} restaurantname={restaurant.name}>
                                <FontAwesomeIcon icon={faUtensils} size="xs" className="mr-1" /> menu
                            </a>
                        </div>
                        {popularDishes && popularDishes.length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs text-gray-500">Top items:</span>
                                {popularDishes.map((dish, i) => (
                                    <span key={i} className="text-xs bg-slate-800 text-orange-200/70 border border-slate-700 rounded px-1.5 py-0.5">
                                        {dish}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="shrink-0 flex-col items-end grid grid-cols-2 gap-2 content-start">
                <div className="text-center">
                    <p className="text-base leading-6 text-white">
                        <FontAwesomeIcon icon={faHeart} className="cursor-pointer hover:text-red-500"
                                         clicked="false" restaurantid={restaurant.id}
                                         restaurantname={restaurant.name} size="lg" />
                    </p>
                    <p className="text-base leading-6 text-white mt-2">
                        <a href={mapsUrl} target="_blank" rel="noreferrer"
                           className="font-medium text-white hover:text-orange-400"
                           restaurantid={restaurant.id} restaurantname={restaurant.name}>
                            <FontAwesomeIcon icon={faLink} size="lg" />
                        </a>
                    </p>
                </div>
                <div>
                    <p className="text-base leading-6 text-white"><span id={restaurant.id}>0</span></p>
                    <p className="text-base leading-6 text-white mt-2"><span id={`c_${restaurant.id}`}>0</span></p>
                </div>
            </div>
        </div>
    );
};

export default RestaurantCard;
